import { type MouseEvent, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Badge, IconButton } from '@socialchorus/shared-ui-components';
import Spinner from '../../../../components/ui/spinner';
import type { ContentVideo } from '../../../../models/user-generated-contents/types';
import { MAX_VIDEOS } from './use-video-uploader';
import { useSortableItem, SortableGrid } from './sortable';
import './videos.scss';

type VideoItemProps = {
  video: ContentVideo;
  index: number;
  onDelete: (index: number) => void;
};

const SortableVideoItem = ({ video, index, onDelete }: VideoItemProps) => {
  const { t } = useTranslation();
  const { setNodeRef, style, attributes, listeners } = useSortableItem(
    video.url ?? `video-${index}`
  );
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const renderThumbnail = () => {
    if (video.status === 'completed' && video.preview_image_url) {
      return (
        <img
          className="ugc-videos__thumbnail"
          src={video.preview_image_url}
          alt={t('user_generated_content.media.videos.video_thumbnail', {
            index: index + 1,
          })}
          draggable={false}
        />
      );
    }
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        className="ugc-videos__thumbnail"
        src={`${video.url}#t=0.1`}
        preload="metadata"
        muted
        draggable={false}
        aria-label={t('user_generated_content.media.videos.video_thumbnail', {
          index: index + 1,
        })}
      />
    );
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className="ugc-videos__item"
        {...attributes}
        {...listeners}
      >
        {renderThumbnail()}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          className="ugc-videos__item-overlay"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <IconButton
            iconName="play_circle"
            size="compact"
            className="bg-black-05"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              setIsLightboxOpen(true);
            }}
            aria-label={t('user_generated_content.media.videos.play')}
          />
          <IconButton
            iconName="delete"
            size="compact"
            className="bg-black-05"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              onDelete(index);
            }}
            aria-label={t('user_generated_content.media.videos.delete')}
          />
        </div>
      </div>
      {isLightboxOpen &&
        createPortal(
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            className="ugc-videos__lightbox-overlay"
            onClick={() => setIsLightboxOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setIsLightboxOpen(false);
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className="ugc-videos__lightbox-content"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video className="ugc-videos__lightbox-player" controls autoPlay>
                {video.hls_url && (
                  <source src={video.hls_url} type="application/x-mpegURL" />
                )}
                <source src={video.url} />
              </video>
            </div>
            <IconButton
              iconName="close"
              size="compact"
              className="ugc-videos__lightbox-close"
              onClick={() => setIsLightboxOpen(false)}
              aria-label={t('user_generated_content.media.videos.close')}
            />
          </div>,
          document.body
        )}
    </>
  );
};

type VideosProps = {
  videos: ContentVideo[];
  inProgress: File[];
  totalCount: number;
  onDelete: (index: number) => void;
  onReorder: (videos: ContentVideo[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dragHandleProps?: Record<string, any>;
};

const Videos = ({
  videos,
  inProgress,
  totalCount,
  onDelete,
  onReorder,
  dragHandleProps,
}: VideosProps) => {
  const { t } = useTranslation();

  return (
    <div className="ugc-videos__section">
      <div
        className={`ugc-videos__section-header${
          dragHandleProps ? ' ugc-videos__section-header--draggable' : ''
        }`}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(dragHandleProps as any)}
      >
        <Badge
          text={t('user_generated_content.media.videos.section_label', {
            count: totalCount,
            max: MAX_VIDEOS,
          })}
        />
      </div>
      <SortableGrid<ContentVideo>
        items={videos}
        getId={(vid) => vid.url}
        onReorder={(reordered) =>
          onReorder(
            reordered.map((vid: ContentVideo, i: number) => ({
              ...vid,
              order: i,
            }))
          )
        }
      >
        <div className="ugc-videos__grid">
          {videos.map((video, i) => (
            <SortableVideoItem
              key={video.url}
              video={video}
              index={i}
              onDelete={onDelete}
            />
          ))}
          {inProgress.map((file) => (
            <div
              key={`${file.name}-${file.lastModified}`}
              className="ugc-videos__item ugc-videos__item--uploading"
            >
              <Spinner loading />
              <span className="text-caption">{t('common.uploading')}</span>
            </div>
          ))}
        </div>
      </SortableGrid>
    </div>
  );
};

export default Videos;
